import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, Typography, message, Modal, Select } from 'antd';
import { UserOutlined, MailOutlined, BankOutlined, CloseOutlined, PhoneOutlined } from '@ant-design/icons';
import './App.css';

const { Title, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

/**
 * Backend API URL
 * Uses relative path in production or localhost in development
 */
const BACKEND_API_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : 'http://localhost:4000/api';

const ContactUsForm = () => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [notesLength, setNotesLength] = useState(0);
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [submittedData, setSubmittedData] = useState({
    name: '',
    email: ''
  });
  
  // Prevent scrolling when the component mounts
  useEffect(() => {
    // Save the original styles
    const originalStyle = document.body.style.overflow;
    const originalPosition = document.body.style.position;
    const originalHeight = document.body.style.height;
    
    // Disable scrolling
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';
    
    // Restore original styles when component unmounts
    return () => {
      document.body.style.overflow = originalStyle;
      document.body.style.position = originalPosition;
      document.body.style.height = originalHeight;
    };
  }, []);

  const handleContactSubmit = async (contactUsData) => {
    try {
      const response = await fetch(`${BACKEND_API_URL}/contact-us`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactUsData),
      });
      
      const result = await response.json();
      
      return {
        success: response.ok,
        message: result.message || (response.ok ? 'Contact request submitted successfully' : 'Request failed')
      };

    } catch (error) {
      return {
        success: false,
        message: 'Failed to submit. Please try again.'
      };
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const contactUsData = {
        ...values,
        timestamp: new Date().toISOString()
      };

      // Send data to backend
      const result = await handleContactSubmit(contactUsData);
      
      if (result.success) {
        // Save the submitted data for displaying in the success modal
        setSubmittedData({
          name: values.name,
          email: values.email
        });
        
        // Show the success modal
        setSuccessModalVisible(true);
      } else {
        message.error(result.message || 'Request failed. Please try again.');
      }

    } catch (error) {
      message.error('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/');
  };

  const handleNotesChange = (e) => {
    const value = e.target.value;
    setNotesLength(value.length);
  };
  
  const handleCloseSuccessModal = () => {
    setSuccessModalVisible(false);
    navigate('/');
  };

return (
  <div className="full-page-form" style={{ 
    backgroundColor: '#ffffff',
    height: '100vh',
    width: '100vw',
    display: 'flex',
    alignItems: window.innerWidth <= 480 ? 'flex-start' : 'center',
    justifyContent: 'center',
    overflow: window.innerWidth <= 480 ? 'auto' : 'hidden',
    position: window.innerWidth <= 480 ? 'absolute' : 'fixed',
    top: 0,
    left: 0,
    paddingTop: window.innerWidth <= 480 ? '50px' : 0,
    paddingBottom: window.innerWidth <= 480 ? '20px' : 0
  }}>
    <div className="form-container" style={{ 
      width: '90%',
      maxWidth: '700px',
      backgroundColor: 'white',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
      borderRadius: '8px',
      padding: window.innerWidth <= 480 ? '24px' : '32px',
      margin: window.innerWidth <= 480 ? '0 auto' : 'auto',
      position: 'relative',
      transform: window.innerWidth <= 480 ? 'none' : 'translateY(5%)',
      transition: 'transform 0.3s ease',
      maxHeight: window.innerWidth <= 480 ? 'none' : '100%'
    }}>
        <div style={{ 
          textAlign: 'center', 
          marginBottom: '16px'
        }}>
          <Title level={2} style={{ 
            margin: 0, 
            color: '#1a237e',
            fontWeight: 'bold',
            fontSize: '28px'
          }}>
            Start a Conversation
          </Title>
          
          <Paragraph style={{
            margin: '10px 0 0 0',
            color: '#4B5563',
            fontSize: '18px',
            lineHeight: '1.3',
            textAlign: 'center',
          }}>
            Interested in our services or have any other question?<br />
            Send us a note - you'll hear from us within one business day.
          </Paragraph>
        </div>
        
        <Form
          form={form}
          name="contact_request_form"
          layout="vertical"
          onFinish={onFinish}
          autoComplete="off"
          requiredMark={false}
          className="bold-form-inputs"
        >
          <Form.Item
            name="name"
            rules={[{ required: true, message: 'Name is required' }]}
            style={{ marginBottom: '16px' }}
          >
            <Input 
              prefix={<UserOutlined style={{ color: '#64748B' }} />} 
              placeholder="Name" 
              size="large"
              style={{ borderColor: '#CBD5E1', height: '45px', borderRadius: '6px', fontWeight: '600', color: '#1F2937' }}
            />
          </Form.Item>
          
          <Form.Item
            name="company"
            style={{ marginBottom: '16px' }}
          >
            <Input 
              prefix={<BankOutlined style={{ color: '#64748B' }} />} 
              placeholder="Firm/Organization (optional)" 
              size="large"
              style={{ borderColor: '#CBD5E1', height: '45px', borderRadius: '6px' }}
            />
          </Form.Item>

          <Form.Item
            name="email"
            rules={[
              { required: true, message: 'Email required' },
              { type: 'email', message: 'Invalid email' }
            ]}
            style={{ marginBottom: '16px' }}
          >
            <Input 
              prefix={<MailOutlined style={{ color: '#64748B' }} />} 
              placeholder="email address" 
              size="large"
              style={{ borderColor: '#CBD5E1', height: '45px', borderRadius: '6px' }}
            />
          </Form.Item>

          <Form.Item
            name="phone"
            style={{ marginBottom: '16px' }}
            rules={[
              { pattern: /^[0-9]*$/, message: 'Phone number can only contain digits' }
            ]}
          >
            <Input 
              prefix={<PhoneOutlined style={{ color: '#64748B' }} />} 
              placeholder="Phone number (optional)" 
              size="large"
              style={{ borderColor: '#CBD5E1', height: '45px', borderRadius: '6px' }}
              onKeyPress={(e) => {
                const isDigit = /\d/.test(e.key);
                if (!isDigit) e.preventDefault();
              }}
            />
          </Form.Item>

          <Form.Item
            name="intent"
            rules={[{ required: true, message: 'Please select a reason' }]}
            style={{ marginBottom: '16px' }}
            
          >
            <Select
              placeholder="What can we help you with today?"
              size="large"
              style={{ width: '100%', height: '45px' }}
              optionLabelProp="label"
              className="left-aligned-select"
            >
              <Option value="become_client" label="Become a Client">Become a Client</Option>
              <Option value="learn_about_us" label="Learn About Us and Our Services">Learn About Us and Our Services</Option>
              <Option value="other" label="Other">Other</Option>
            </Select>
          </Form.Item>
          
          <Form.Item
            name="notes"
            rules={[
              { max: 1000, message: 'Notes cannot exceed 1000 characters' }
            ]}
            style={{ marginBottom: '24px' }}
          >
            <TextArea
              placeholder="Note (optional)"
              size="large"
              style={{ 
                borderColor: '#CBD5E1', 
                minHeight: '120px', 
                borderRadius: '6px' 
              }}
              showCount
              maxLength={1000}
              onChange={handleNotesChange}
            />
          </Form.Item>

          <div style={{ 
            display: 'flex', 
            gap: window.innerWidth <= 480 ? '12px' : '60px', // Reduced gap on mobile
            marginBottom: '16px',
            flexDirection: 'row', // Always use row layout
            justifyContent: 'center', 
          }}>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading}
              size={window.innerWidth <= 480 ? 'middle' : 'large'}
              style={{
                flex: '1',
                height: window.innerWidth <= 480 ? '38px' : '45px',
                fontWeight: '500',
                fontSize: window.innerWidth <= 480 ? '14px' : '16px',
                borderRadius: '6px',
                backgroundColor: '#1a237e',
                borderColor: '#1a237e',
                maxWidth: window.innerWidth <= 480 ? '140px' : '220px',
                padding: window.innerWidth <= 480 ? '0 10px' : undefined,
              }}
            >
              Contact Us
            </Button>
            
            <Button 
              onClick={handleCancel}
              size={window.innerWidth <= 480 ? 'middle' : 'large'}
              style={{
                flex: '1',
                height: window.innerWidth <= 480 ? '38px' : '45px',
                fontSize: window.innerWidth <= 480 ? '14px' : '16px',
                borderRadius: '6px',
                borderColor: '#CBD5E1',
                maxWidth: window.innerWidth <= 480 ? '140px' : '220px',
                padding: window.innerWidth <= 480 ? '0 10px' : undefined,
              }}
            >
              Cancel
            </Button>
          </div>
        </Form>
      </div>
      
      {/* Success Modal */}
      <Modal
        title={null}
        open={successModalVisible}
        footer={null}
        closable={true}
        onCancel={handleCloseSuccessModal}
        maskClosable={false}
        width={600}
        closeIcon={
          <CloseOutlined style={{ 
            fontSize: '20px',
            color: '#64748B'
          }} />
        }
        bodyStyle={{ 
          padding: '50px 40px',
          textAlign: 'center'
        }}
        style={{
          top: '80px'
        }}
      >
        <div style={{ marginBottom: '32px' }}>
          <div style={{ width: '90px', height: '90px', margin: '0 auto 24px', borderRadius: '50%', backgroundColor: '#E8F5E9', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <svg width="50" height="50" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 6L9 17L4 12" stroke="#4CAF50" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <Title level={2} style={{ margin: 0, color: '#1a237e', marginBottom: '16px' }}>
            Thank You, {submittedData.name}!
          </Title>
          <Paragraph style={{ fontSize: '18px', color: '#4B5563', margin: 0, lineHeight: '1.6' }}>
            We've received your request. We'll be in touch with you at <strong>{submittedData.email}</strong>.
          </Paragraph>
        </div>
        
        <Button
          type="primary"
          size="large"
          onClick={handleCloseSuccessModal}
          style={{
            height: '54px',
            fontWeight: '500',
            fontSize: '18px',
            width: '100%',
            borderRadius: '6px',
            backgroundColor: '#1a237e',
            borderColor: '#1a237e',
            marginTop: '16px'
          }}
        >
          Close
        </Button>
      </Modal>
    </div>
  );
};

export default ContactUsForm;
